import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Heading,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useParams } from 'react-router-dom';
import { useClient, useMutation, useQuery } from 'urql';
import {
  Select as ChakraReactSelect,
  ChakraStylesConfig,
} from 'chakra-react-select';
import { useTranslation } from 'react-i18next';
import {
  faCaretDown,
  faCaretRight,
  faLink,
  faShare,
} from '@fortawesome/free-solid-svg-icons';
import {
  CreateInitialStateDocument,
  CreateSavedViewDocument,
  GetExternalAccessTokenDocument,
  GetProjectDeepDocument,
  InitialStateInput,
  CreateSharedLinkWithDurationDocument,
} from '../../gql/graphql';
import { useUserTenant } from '../../services/auth-info';
import { EventName, useMixpanel } from '../../services/mixpanel';
import { useBreadcrumb } from '../common/BreadcrumbProvider';
import SparkelIcon from '../common/icon/SparkelIcon';
import { useViewManager } from '../common/ViewManagerContext';

type ExpiresInChoice = {
  label: string;
  value: number;
};

type ProjectPathParams = {
  projectId: string;
};

// eslint-disable-next-line complexity
export default function OrderSharingPopover() {
  const client = useClient();
  const [error, setError] = useState<string | null>();
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  const { t } = useTranslation('project');

  const { orderId: currentOrderId, projectName, orderName } = useBreadcrumb();

  // Generate a list of choices for the expiry time
  const EXPIRY_CHOICES: ExpiresInChoice[] = useMemo(
    () => [
      {
        label: t('table-sharing-modal.expiration.5-minutes'),
        value: 60 * 60,
      },
      {
        label: t('table-sharing-modal.expiration.24-hours'),
        value: 24 * 60 * 60,
      },
      {
        label: t('table-sharing-modal.expiration.7-days'),
        value: 7 * 24 * 60 * 60,
      },
      {
        label: t('table-sharing-modal.expiration.14-days'),
        value: 14 * 24 * 60 * 60,
      },
      {
        label: t('table-sharing-modal.expiration.28-days'),
        value: 28 * 24 * 60 * 60,
      },
      {
        label: t('table-sharing-modal.expiration.3-months'),
        value: 90 * 24 * 60 * 60,
      },
    ],
    [t]
  );

  const [expiresIn, setExpiresIn] = useState<number>(EXPIRY_CHOICES[2].value);
  const [, setAccessToken] = useState<string | null>(null);
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);

  const { projectId } = useParams<
    keyof ProjectPathParams
  >() as ProjectPathParams;

  const { tenant } = useUserTenant();

  const { trackEvent } = useMixpanel();

  //const { savedViews, createSavedView } = useSavedViews(projectId, false);

  const {
    getCurrentView,
    getCurrentItemIdSelection,
    getCurrentExpandedItemIds,
  } = useViewManager();

  const [{ data: projectData }] = useQuery({
    query: GetProjectDeepDocument,
    variables: { id: projectId },
  });

  const orders = useMemo(() => {
    if (projectData?.project?.orders) {
      return projectData.project.orders.map((order) => ({
        id: order.id,
        name: order.name,
        isAggregatedOrder: !!order.isAggregatedOrder,
      }));
    }
    return [];
  }, [projectData?.project?.orders]);

  const currentOrder =
    useMemo(
      () => orders.find((order) => order.id === currentOrderId) || undefined,
      [currentOrderId, orders]
    ) || null;

  const defaultSelectedOrders = useMemo(() => {
    if (!currentOrder) return [];
    if (currentOrder.isAggregatedOrder) {
      const nonAggregatedOrders = orders.filter(
        (order) => !order.isAggregatedOrder
      );
      return [currentOrder, ...nonAggregatedOrders];
    }
    return [currentOrder];
  }, [currentOrder, orders]);

  const [selectedOrders, setSelectedOrders] = useState(defaultSelectedOrders);

  // Reset selected orders when the current order changes
  useEffect(() => {
    setSelectedOrders(defaultSelectedOrders);
  }, [defaultSelectedOrders]);

  const { isOpen, onClose, onToggle } = useDisclosure();

  const [hasCopied, setHasCopied] = useState(false);

  useEffect(() => {
    if (hasCopied) {
      trackEvent(EventName.TableShared, {
        'Project Name': projectName,
        'Project ID': projectId,
        'Table Name': orderName,
        'Expiration Duration': EXPIRY_CHOICES.find(
          (choice) => choice.value === expiresIn
        )?.label,
      });
    }
  }, [
    EXPIRY_CHOICES,
    expiresIn,
    hasCopied,
    orderName,
    projectId,
    projectName,
    tenant?.name,
    trackEvent,
  ]);

  const fetchToken = useCallback(
    async (expiresIn: number) => {
      const result = await client.query(
        GetExternalAccessTokenDocument,
        {
          orderIds: selectedOrders.map((order) => order.id),
          expiresIn,
        },
        { requestPolicy: 'network-only' }
      );
      if (!result.data?.externalAccessToken?.accessToken) {
        console.error('Could not get access token', result.error);
        setError('Could not get access token');
      } else {
        const accessToken = result.data?.externalAccessToken?.accessToken;
        setAccessToken(accessToken);

        return accessToken;
      }
    },
    [client, selectedOrders]
  );

  const [, executeCreateSavedViewMutation] = useMutation(
    CreateSavedViewDocument
  );

  const [, createInitialState] = useMutation(CreateInitialStateDocument);
  const [, createSharedLinkWithDurationFn] = useMutation(
    CreateSharedLinkWithDurationDocument
  );

  const toast = useToast();

  // setError from useState, aliasing to avoid conflict if helpers were outside
  const setPopoverError = setError;

  const handleGenerateLinkClick = useCallback(async () => {
    setIsGeneratingLink(true);
    setPopoverError(null);

    // Helper: Create Saved View
    const createSavedViewHelper = async () => {
      const currentView = await getCurrentView();
      const savedViewResult = await executeCreateSavedViewMutation({
        input: {
          savedView: {
            projectId,
            name: uuid(),
            cameraPosition: currentView.cameraPosition,
            categories: currentView.categories,
            layers: currentView.layers,
            statuses: currentView.statuses,
            hiddenElements: currentView.hiddenElements,
            hiddenModels: currentView.hiddenModels,
            isolatedElements: currentView.isolatedElements,
            isCommentView: false,
          },
        },
      });
      const savedViewId = savedViewResult.data?.createSavedView?.savedView?.id;
      if (!savedViewId) throw new Error('Could not create view');
      return savedViewId;
    };

    // Helper: Prepare InitialStateInput
    const prepareInitialStateInputHelper = async (
      savedViewId: string,
      token: string
    ): Promise<InitialStateInput> => {
      const isIsolationToggleActive =
        window.localStorage.getItem('isolate-linked-elements') === 'true';
      const isShowingShapeLabels =
        window.localStorage.getItem('shape-labels') === 'true';
      const ghostingSettings = JSON.parse(
        window.localStorage.getItem(
          'Autodesk.Viewing.Private.GuiViewer3D.SavedSettings.AEC'
        ) ?? '{}' // Default to empty object string for JSON.parse
      );
      const ghosting = ghostingSettings.ghosting ?? false;
      const orthographic =
        window.localStorage.getItem('orthographic-mode') === 'true';
      const selectedOrderEntries = await getCurrentItemIdSelection();
      const expandedOrderEntries = await getCurrentExpandedItemIds();

      return {
        projectId,
        selectedOrderEntries,
        expandedOrderEntries,
        viewSettings: {
          ghosting,
          focusMode: isIsolationToggleActive,
          showLabels: isShowingShapeLabels,
          orthographicMode: orthographic,
        },
        viewId: savedViewId,
        externalAccessToken: token,
      };
    };

    // Helper: Create Initial State entity
    const createInitialStateEntityHelper = async (
      initialStateInput: InitialStateInput
    ) => {
      const result = await createInitialState({
        input: { initialState: initialStateInput },
      });
      if (result.error) throw new Error('Could not create initial state');
      const id = result.data?.createInitialState?.initialState?.id;
      if (!id)
        throw new Error('Could not get initial state ID after creation.');
      return id;
    };

    // Helper: Create SharedLink database entry
    const recordSharedLinkHelper = async (
      initialStateIdValue: string,
      currentExpiresIn: number,
      currentProjectName: string | undefined,
      currentSelectedOrders: typeof selectedOrders,
      currentOrderName?: string
    ) => {
      const pName = currentProjectName || 'Shared View';
      let sharedLinkNameStr = pName;
      if (currentSelectedOrders.length === 1 && currentSelectedOrders[0].name) {
        sharedLinkNameStr = `${pName} - ${currentSelectedOrders[0].name}`;
      } else if (currentSelectedOrders.length > 1) {
        const orderNamesStr = currentSelectedOrders
          .slice(0, 2)
          .map((o: { name: string }) => o.name)
          .join(', ');
        if (currentSelectedOrders.length > 2) {
          sharedLinkNameStr = `${pName} - ${orderNamesStr}, and ${
            currentSelectedOrders.length - 2
          } more`;
        } else {
          sharedLinkNameStr = `${pName} - ${orderNamesStr}`;
        }
      } else if (currentOrderName) {
        sharedLinkNameStr = `${pName} - ${currentOrderName}`;
      }

      try {
        const mutationVariables = {
          projectId: projectId,
          initialStateId: initialStateIdValue,
          name: sharedLinkNameStr,
          expiresInSeconds: currentExpiresIn,
          orderIds: currentSelectedOrders.map((order) => order.id),
        };

        const createSharedLinkResult = await createSharedLinkWithDurationFn(
          mutationVariables
        );

        if (createSharedLinkResult.error) {
          console.error(
            'Error creating shared link entry via function:',
            createSharedLinkResult.error
          );
          toast({
            title: 'Link created, but failed to save to management list',
            description: createSharedLinkResult.error.message,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (e: unknown) {
        console.error('Exception creating shared link entry:', e);
        toast({
          title:
            'Link created, but encountered an exception saving to management list',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    try {
      const savedViewId = await createSavedViewHelper();
      const externalAccessToken = await fetchToken(expiresIn);
      if (!externalAccessToken) throw new Error('Could not get access token');

      const initialStateInput = await prepareInitialStateInputHelper(
        savedViewId,
        externalAccessToken
      );

      const finalInitialStateId = await createInitialStateEntityHelper(
        initialStateInput
      );

      await recordSharedLinkHelper(
        finalInitialStateId,
        expiresIn,
        projectName,
        selectedOrders,
        orderName
      );

      await window.navigator.clipboard.writeText(
        `${window.location.origin}/view/${finalInitialStateId}`
      );
      setHasCopied(true);
      toast({
        title: t('table-sharing-popover.success'),
        status: 'success',
      });
    } catch (err: unknown) {
      console.error('Error generating share link:', err);
      const message =
        err instanceof Error
          ? err.message
          : 'An unexpected error occurred while generating the link.';
      setPopoverError(message);
    } finally {
      setIsGeneratingLink(false);
    }
  }, [
    getCurrentView,
    executeCreateSavedViewMutation,
    projectId,
    fetchToken,
    expiresIn, // Make sure expiresIn is stable or correctly listed as dependency
    getCurrentItemIdSelection,
    getCurrentExpandedItemIds,
    createInitialState,
    createSharedLinkWithDurationFn,
    toast,
    t,
    projectName, // Stable prop or listed as dependency
    orderName, // Stable prop or listed as dependency
    selectedOrders, // Stable state or listed as dependency
    setPopoverError, // Added setError alias to dependencies
  ]);

  let modalBodyElement: React.ReactElement | null = null;

  if (error) {
    modalBodyElement = (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  } else {
    modalBodyElement = (
      <VStack padding={1} marginTop={3} spacing={3}>
        {selectedOrders.length > 0 && (
          <>
            <Button
              onClick={handleGenerateLinkClick}
              isLoading={isGeneratingLink}
              colorScheme="gray"
              leftIcon={<SparkelIcon icon={faLink} color={'inherit'} />}
              size={'xs'}
              width="100%"
            >
              {hasCopied
                ? t('table-sharing-modal.link-copied')
                : t('table-sharing-modal.copy-link')}
            </Button>
          </>
        )}
        <Button
          size={'xs'}
          onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
          variant={'ghost'}
          colorScheme="gray"
          fontWeight={'normal'}
          color={'gray.500'}
          alignSelf={'flex-start'}
          leftIcon={
            <SparkelIcon
              icon={isSettingsExpanded ? faCaretDown : faCaretRight}
              color={'inherit'}
            />
          }
        >
          {isSettingsExpanded
            ? t('table-sharing-popover.hide-settings')
            : t('table-sharing-popover.show-settings')}
        </Button>
        {isSettingsExpanded && (
          <>
            <Text alignSelf="flex-start" fontSize={'xs'} color={'gray.500'}>
              {t('table-sharing-modal.select-tables')}
            </Text>
            <Box width="100%">
              <ChakraReactSelect
                isMulti
                isInvalid={selectedOrders.length === 0}
                closeMenuOnSelect={false}
                chakraStyles={chakraStyles}
                tagVariant="solid"
                variant="filled"
                value={selectedOrders.map((order) => ({
                  value: order.id,
                  label: order.name,
                }))}
                options={orders.map((order) => ({
                  value: order.id,
                  label: order.name,
                }))}
                onChange={(selectedOptions) => {
                  const selectedIds = selectedOptions.map(
                    (option) =>
                      (option as { value: string; label: string }).value
                  );
                  const idToOrder = new Map(
                    orders.map((o) => [o.id, o] as const)
                  );
                  const nextSelected = selectedIds
                    .map((id) => idToOrder.get(id))
                    .filter((o): o is typeof orders[number] => Boolean(o));
                  setSelectedOrders(nextSelected);
                }}
              />
            </Box>
            <Text alignSelf="flex-start" fontSize={'xs'} color={'gray.500'}>
              {t('table-sharing-modal.expiration.label')}
            </Text>
            <Select
              value={expiresIn}
              variant={'filled'}
              size={'xs'}
              borderRadius={'md'}
              onChange={(event) =>
                void setExpiresIn(Number.parseInt(event.target.value))
              }
            >
              {EXPIRY_CHOICES.map((choice) => (
                <option key={choice.label} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </Select>
          </>
        )}
      </VStack>
    );
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => {
        setHasCopied(false);
        onClose();
      }}
      isLazy
      closeOnBlur
    >
      <PopoverTrigger>
        <Button
          leftIcon={<SparkelIcon icon={faShare} color={'inherit'} />}
          size={'sm'}
          borderRadius={'md'}
          onClick={onToggle}
          isDisabled={!currentOrderId}
        >
          Share
        </Button>
      </PopoverTrigger>
      <Portal containerRef={{ current: document.body }}>
        <PopoverContent
          borderRadius={'lg'}
          shadow={'2xl'}
          width={'2xs'}
          zIndex={999}
        >
          <PopoverBody>
            <Heading as={'h3'} size={'xs'} marginY={1}>
              {t('table-sharing-modal.title')}
            </Heading>
            {modalBodyElement}
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

const chakraStyles: ChakraStylesConfig = {
  control: (provided) => {
    return {
      ...provided,
      borderRadius: 'md',
      minHeight: 6,
      overflowY: 'auto',
    };
  },
  valueContainer: (provided) => {
    return {
      ...provided,
      paddingLeft: 2,
      fontSize: 'xs',
      marginTop: -0.5,
      paddingRight: 0,
      paddingY: 1,
    };
  },
  multiValue: (provided) => {
    return {
      ...provided,
      fontSize: 'xs',
      paddingTop: 0.5,
      paddingBottom: 0,
      minHeight: 0,
      minWidth: 'fit-content',
    };
  },
  multiValueRemove: (provided) => {
    return {
      ...provided,
      paddingY: 0,
    };
  },
  clearIndicator: (provided) => {
    return {
      ...provided,
      padding: 0,
      width: 4,
      height: 4,
      fontSize: '0.5rem',
      colorScheme: 'gray',
    };
  },
  dropdownIndicator: (provided) => {
    return {
      ...provided,
      marginX: 0,
      padding: 0,
      paddingBottom: 1,
      marginTop: '2px',
    };
  },
  noOptionsMessage: (provided) => {
    return {
      ...provided,
      fontSize: 'xs',
    };
  },
  menuList: (provided) => {
    return {
      ...provided,
      borderRadius: 'md',
      padding: 1,
    };
  },
  menu: (provided) => {
    return {
      ...provided,
      minWidth: 200,
    };
  },
  option: (provided, state) => {
    return {
      ...provided,
      fontSize: 'xs',
      borderRadius: 'lg',
      paddingY: 0,
      paddingLeft: 2,
      minHeight: 7,
      fontWeight: state.isSelected ? 'bold' : 'normal',
    };
  },
};
